import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, ArrowRight, Sparkles, ExternalLink, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface QuizStep {
  question: string;
  options: { label: string; value: string }[];
}

const steps: QuizStep[] = [
  {
    question: 'What do you need AI for?',
    options: [
      { label: 'Writing & Content', value: 'writing' },
      { label: 'Coding & Development', value: 'coding' },
      { label: 'Research & Learning', value: 'research' },
      { label: 'Design & Creative', value: 'creative' },
    ],
  },
  {
    question: "What's your budget?",
    options: [
      { label: 'Free only', value: 'Free' },
      { label: 'Freemium is fine', value: 'Freemium' },
      { label: 'Willing to pay', value: 'Paid' },
      { label: 'No preference', value: 'any' },
    ],
  },
  {
    question: 'What matters most to you?',
    options: [
      { label: 'Speed & simplicity', value: 'speed' },
      { label: 'Advanced features', value: 'features' },
      { label: 'Accuracy & quality', value: 'quality' },
      { label: 'Privacy & security', value: 'privacy' },
    ],
  },
];

interface BotResult {
  id: string;
  name: string;
  description: string;
  emoji: string;
  rating: number | null;
  pricing: string | null;
  use_case: string | null;
  website_url: string | null;
  tags: string[] | null;
  key_features: string[] | null;
}

const USE_CASE_MAP: Record<string, string[]> = {
  writing: ['Writing', 'Content', 'Marketing', 'Copywriting'],
  coding: ['Coding', 'Development', 'Programming', 'Code'],
  research: ['Research', 'Learning', 'Education', 'Academic'],
  creative: ['Design', 'Creative', 'Art', 'Image', 'Video'],
};

export const AIToolFinderQuiz: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<BotResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSelect = (value: string) => {
    setAnswers({ ...answers, [currentStep]: value });
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      fetchResults();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const fetchResults = async () => {
    setLoading(true);
    setShowResults(true);

    const useCase = answers[0] || '';
    const budget = answers[1] || '';

    try {
      let query = supabase.from('bots').select('id, name, description, emoji, rating, pricing, use_case, website_url, tags, key_features');

      // Filter by use case using tags or use_case field
      const useCaseTerms = USE_CASE_MAP[useCase] || [];
      if (useCaseTerms.length > 0) {
        const orConditions = useCaseTerms
          .map(term => `use_case.ilike.%${term}%,description.ilike.%${term}%,name.ilike.%${term}%`)
          .join(',');
        query = query.or(orConditions);
      }

      // Filter by budget
      if (budget !== 'any' && budget) {
        query = query.ilike('pricing', `%${budget}%`);
      }

      query = query.order('rating', { ascending: false, nullsFirst: false }).limit(6);

      const { data, error } = await query;

      if (error) throw error;

      // If we got results, use them; otherwise fall back to top-rated bots
      if (data && data.length > 0) {
        setResults(data);
      } else {
        // Fallback: get top-rated bots regardless of filters
        const { data: fallback } = await supabase
          .from('bots')
          .select('id, name, description, emoji, rating, pricing, use_case, website_url, tags, key_features')
          .order('rating', { ascending: false, nullsFirst: false })
          .limit(6);
        setResults(fallback || []);
      }
    } catch (err) {
      console.error('Quiz results error:', err);
      // Fallback
      const { data: fallback } = await supabase
        .from('bots')
        .select('id, name, description, emoji, rating, pricing, use_case, website_url, tags, key_features')
        .order('rating', { ascending: false, nullsFirst: false })
        .limit(6);
      setResults(fallback || []);
    } finally {
      setLoading(false);
    }
  };

  if (showResults) {
    const useCase = answers[0] || '';
    const budget = answers[1] || '';
    const priority = answers[2] || '';

    return (
      <div className="max-w-2xl mx-auto space-y-6 py-8">
        <div className="text-center space-y-3">
          <Sparkles className="w-10 h-10 mx-auto text-foreground" />
          <h2 className="text-2xl font-bold">Your Personalized Recommendations</h2>
          <p className="text-muted-foreground">
            Based on your preferences: <span className="text-foreground font-medium">{steps[0].options.find(o => o.value === useCase)?.label}</span>
            {budget !== 'any' && <>, <span className="text-foreground font-medium">{budget}</span></>}
            {priority && <>, prioritizing <span className="text-foreground font-medium">{steps[2].options.find(o => o.value === priority)?.label?.toLowerCase()}</span></>}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-foreground border-t-transparent rounded-full mx-auto mb-3" />
            <p className="text-muted-foreground">Finding the best tools for you...</p>
          </div>
        ) : results.length > 0 ? (
          <div className="grid gap-4">
            {results.map((bot, index) => (
              <Card
                key={bot.id}
                className="p-4 cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5"
                onClick={() => navigate(`/bot/${bot.id}`)}
              >
                <div className="flex items-start gap-4">
                  <div className="text-3xl shrink-0">{bot.emoji}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{bot.name}</h3>
                      {index === 0 && (
                        <span className="text-xs bg-foreground text-background px-2 py-0.5 rounded-full font-medium">
                          Best Match
                        </span>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm line-clamp-2 mb-2">{bot.description}</p>
                    <div className="flex items-center gap-3 text-sm">
                      {bot.rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 fill-foreground text-foreground" />
                          {bot.rating.toFixed(1)}
                        </span>
                      )}
                      {bot.pricing && (
                        <span className="text-muted-foreground">{bot.pricing}</span>
                      )}
                      {bot.website_url && (
                        <a
                          href={bot.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center gap-1 text-accent hover:underline"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Visit
                        </a>
                      )}
                    </div>
                    {bot.key_features && bot.key_features.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {bot.key_features.slice(0, 3).map((f, i) => (
                          <span key={i} className="text-xs bg-secondary text-secondary-foreground px-2 py-0.5 rounded">
                            {f}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No exact matches found. Try different preferences.</p>
          </div>
        )}

        <div className="flex gap-3 justify-center pt-4">
          <Button variant="outline" onClick={() => { setShowResults(false); setCurrentStep(0); setAnswers({}); setResults([]); }}>
            Retake Quiz
          </Button>
          <Button onClick={() => navigate('/')}>
            Browse All Tools
          </Button>
        </div>
      </div>
    );
  }

  const step = steps[currentStep];

  return (
    <div className="max-w-lg mx-auto space-y-6">
      {/* Progress */}
      <div className="flex gap-2">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i <= currentStep ? 'bg-foreground' : 'bg-border'
            }`}
          />
        ))}
      </div>

      <h2 className="text-2xl font-bold">{step.question}</h2>

      <div className="grid grid-cols-1 gap-3">
        {step.options.map((opt) => (
          <Card
            key={opt.value}
            className={`p-4 cursor-pointer transition-all border-2 ${
              answers[currentStep] === opt.value
                ? 'border-foreground bg-secondary'
                : 'border-border hover:border-muted-foreground'
            }`}
            onClick={() => handleSelect(opt.value)}
          >
            <span className="font-medium">{opt.label}</span>
          </Card>
        ))}
      </div>

      <div className="flex justify-between">
        <Button variant="ghost" onClick={handleBack} disabled={currentStep === 0}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button onClick={handleNext} disabled={!answers[currentStep]}>
          {currentStep === steps.length - 1 ? 'See Results' : 'Next'}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};
